import { apiErrorSchema, type ErrorCode } from '@koderek/shared';
import type { z } from 'zod';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly body: unknown,
    readonly code: ErrorCode | undefined,
  ) {
    super(`Błąd API: ${status}${code ? ` (${code})` : ''}`);
  }

  /** Szczegóły błędu walidacji albo inne dane z pola `details`. */
  get details(): unknown {
    return (this.body as { details?: unknown } | null)?.details;
  }
}

type Method = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

/**
 * Wywołanie API pod tą samą domeną (`/api/...`, ADR 03 §2.2). Ciasteczko sesji idzie automatycznie.
 * Odpowiedź jest sprawdzana schematem Zod z `@koderek/shared`; błąd ma kod z `errorCodes`.
 */
export async function api<T = void>(
  method: Method,
  path: string,
  options: { body?: unknown; schema?: z.ZodType<T> } = {},
): Promise<T> {
  const res = await fetch(`/api${path}`, {
    method,
    headers: {
      Accept: 'application/json',
      ...(options.body !== undefined ? { 'Content-Type': 'application/json' } : {}),
    },
    body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
    credentials: 'same-origin',
  });
  const text = await res.text();
  const body: unknown = text ? safeJson(text) : null;
  if (!res.ok) {
    const parsed = apiErrorSchema.safeParse(body);
    throw new ApiError(res.status, body, parsed.success ? parsed.data.code : undefined);
  }
  return options.schema ? options.schema.parse(body) : (body as T);
}

export function apiGet<T>(path: string, schema: z.ZodType<T>): Promise<T> {
  return api('GET', path, { schema });
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}
